import { TranslateModule } from '@ngx-translate/core';
import { LogService } from '@services/util/log.service';
import { DatePickerModule } from 'primeng/datepicker';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, EventEmitter, input, OnInit, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

/**
 * Component for handling date fields in parking request form
 * Manages start and end dates with readonly state and validation
 */
@Component({
  selector: 'app-parking-request-date-fields',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, DatePickerModule],
  template: `
    <!-- Date Range -->
    <div class="flex flex-col gap-4 md:flex-row">
      <!-- Start Date -->
      <div class="field flex-1">
        <label class="mb-2 block font-medium">{{ 'parking.form.startDate' | translate }} *</label>
        <p-datepicker
          [(ngModel)]="startDateValue"
          name="startDate"
          #startDateRef="ngModel"
          [ngModelOptions]="{ standalone: true }"
          required
          [disabled]="startDateReadonly()"
          (ngModelChange)="onStartDateChange($event)"
          dateFormat="yy-mm-dd"
          [showIcon]="!startDateReadonly()"
          [readonlyInput]="true"
          [showButtonBar]="!startDateReadonly()"
          [minDate]="minDateValue"
          [maxDate]="maxDateValue"
          [styleClass]="'w-full' + (dateRangeError() ? ' p-invalid' : '')"
        >
        </p-datepicker>
        @if (startDateRef.hasError('required') && startDateRef.touched) {
          <small class="text-red-500">{{ 'parking.form.startDateRequired' | translate }}</small>
        }
        @if (startDateCorrected()) {
          <small class="mt-1 flex items-center text-blue-600">
            <i class="pi pi-info-circle mr-1"></i>
            {{ 'parking.form.datesAutomaticallyAdjusted' | translate }}
          </small>
        }
      </div>

      <!-- End Date -->
      <div class="field flex-1">
        <label class="mb-2 block font-medium">{{ 'parking.form.endDate' | translate }} *</label>
        <p-datepicker
          [(ngModel)]="endDateValue"
          name="endDate"
          #endDateRef="ngModel"
          [ngModelOptions]="{ standalone: true }"
          required
          [disabled]="endDateReadonly()"
          (ngModelChange)="onEndDateChange($event)"
          dateFormat="yy-mm-dd"
          [showIcon]="!endDateReadonly()"
          [readonlyInput]="true"
          [showButtonBar]="!endDateReadonly()"
          [minDate]="minDateValue"
          [maxDate]="maxDateValue"
          [styleClass]="'w-full' + (dateRangeError() ? ' p-invalid' : '')"
        >
        </p-datepicker>
        @if (endDateRef.hasError('required') && endDateRef.touched) {
          <small class="text-red-500">{{ 'parking.form.endDateRequired' | translate }}</small>
        }
        @if (endDateCorrected()) {
          <small class="mt-1 flex items-center text-blue-600">
            <i class="pi pi-info-circle mr-1"></i>
            {{ 'parking.form.datesAutomaticallyAdjusted' | translate }}
          </small>
        }
      </div>
    </div>

    <!-- Date corrections message -->
    @if (dateCorrectionsMessage()) {
      <div class="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3">
        <div class="flex items-start">
          <i class="pi pi-info-circle mr-2 mt-0.5 text-blue-600"></i>
          <div class="text-sm text-blue-800">
            <p class="mb-1 font-medium">{{ 'parking.form.datesAdjusted' | translate }}</p>
            <p>{{ dateCorrectionsMessage() }}</p>
          </div>
        </div>
      </div>
    }

    <!-- Date range validation error -->
    @if (dateRangeError()) {
      <div class="mt-2 rounded-md border border-red-200 bg-red-50 p-3">
        <div class="flex items-start">
          <i class="pi pi-exclamation-triangle mr-2 mt-0.5 text-red-600"></i>
          <div class="text-sm text-red-800">
            <p class="font-medium">{{ dateRangeErrorMessage() | translate }}</p>
          </div>
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ParkingRequestDateFieldsComponent implements OnInit {
  // Input signals
  startDateReadonly = input<boolean>(false);
  endDateReadonly = input<boolean>(false);
  initialStartDate = input<Date | null>(null);
  initialEndDate = input<Date | null>(null);

  // Primitive date constraint properties (no signals - they break PrimeNG DatePicker)
  minDateValue: Date | null = null;
  maxDateValue: Date | null = null;

  // Output events
  @Output() startDateChange = new EventEmitter<Date | null>();
  @Output() endDateChange = new EventEmitter<Date | null>();

  // Component state
  startDateValue: Date | null = null;
  endDateValue: Date | null = null;

  // Date correction signals
  startDateCorrected = signal<boolean>(false);
  endDateCorrected = signal<boolean>(false);
  dateCorrectionsMessage = signal<string>('');

  // Date range validation
  dateRangeError = signal<boolean>(false);
  dateRangeErrorMessage = signal<string>('');

  constructor(
    private cdr: ChangeDetectorRef,
    private log: LogService
  ) {
    // Effect to watch for changes in initialStartDate and update startDateValue
    effect(() => {
      const newStartDate = this.initialStartDate();
      if (newStartDate !== this.startDateValue) {
        this.startDateValue = newStartDate;
        // Force change detection to ensure PrimeNG DatePicker updates
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 0);
      }
    });

    // Effect to watch for changes in initialEndDate and update endDateValue
    effect(() => {
      const newEndDate = this.initialEndDate();
      if (newEndDate !== this.endDateValue) {
        this.endDateValue = newEndDate;
        // Force change detection to ensure PrimeNG DatePicker updates
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 0);
      }
    });
  }

  ngOnInit(): void {
    // Set initial values if provided (effects will handle updates after this)
    if (this.initialStartDate()) {
      this.startDateValue = this.initialStartDate();
    }

    if (this.initialEndDate()) {
      this.endDateValue = this.initialEndDate();
    }
  }

  /**
   * Handle start date change from template-driven form
   */
  onStartDateChange(date: Date | null): void {
    this.log.debug('[DateFields] Start date changed:', date);
    this.startDateValue = date;
    this.validateDateRange(); // Validate after date change
    this.startDateChange.emit(date);
  }

  /**
   * Handle end date change from template-driven form
   */
  onEndDateChange(date: Date | null): void {
    this.log.debug('[DateFields] End date changed:', date);
    this.endDateValue = date;
    this.validateDateRange(); // Validate after date change
    this.endDateChange.emit(date);
  }

  /**
   * Update start date value from parent (for programmatic changes)
   */
  updateStartDate(date: Date | null): void {
    this.startDateValue = date;
  }

  /**
   * Update end date value from parent (for programmatic changes)
   */
  updateEndDate(date: Date | null): void {
    this.endDateValue = date;
  }

  /**
   * Show date correction notification
   */
  showDateCorrection(type: 'start' | 'end', message: string): void {
    if (type === 'start') {
      this.startDateCorrected.set(true);
    } else {
      this.endDateCorrected.set(true);
    }

    this.dateCorrectionsMessage.set(message);

    // Clear message after 10 seconds
    setTimeout(() => {
      this.dateCorrectionsMessage.set('');
      this.startDateCorrected.set(false);
      this.endDateCorrected.set(false);
    }, 10000);
  }

  /**
   * Get current start date value
   */
  getStartDate(): Date | null {
    return this.startDateValue;
  }

  /**
   * Get current end date value
   */
  getEndDate(): Date | null {
    return this.endDateValue;
  }

  /**
   * Update date constraints (called by parent component)
   */
  updateDateConstraints(minDate: Date | null, maxDate: Date | null): void {
    this.minDateValue = minDate;
    this.maxDateValue = maxDate;
  }

  /**
   * Check if date fields have validation errors
   */
  hasValidationErrors(): boolean {
    return this.dateRangeError() || this.hasBookingDateRangeErrors();
  }

  /**
   * Check if dates are within booking date range
   */
  hasBookingDateRangeErrors(): boolean {
    if (!this.startDateValue || !this.endDateValue || !this.minDateValue || !this.maxDateValue) {
      return false; // Can't validate without all required data
    }

    const startDate = new Date(this.startDateValue);
    const endDate = new Date(this.endDateValue);
    const minDate = new Date(this.minDateValue);
    const maxDate = new Date(this.maxDateValue);

    // Set time to start of day for accurate date comparison
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    minDate.setHours(0, 0, 0, 0);
    maxDate.setHours(0, 0, 0, 0);

    // Check if start date is outside booking range
    const startOutOfRange = startDate < minDate || startDate > maxDate;
    // Check if end date is outside booking range
    const endOutOfRange = endDate < minDate || endDate > maxDate;

    return startOutOfRange || endOutOfRange;
  }

  /**
   * Validate date range: end date must be same or later than start date
   */
  private validateDateRange(): void {
    if (!this.startDateValue || !this.endDateValue) {
      // Clear error if either date is missing
      this.dateRangeError.set(false);
      this.dateRangeErrorMessage.set('');
      return;
    }

    // Compare dates (ignore time, compare only dates)
    const startDate = new Date(this.startDateValue);
    const endDate = new Date(this.endDateValue);

    // Set time to start of day for accurate date comparison
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (endDate < startDate) {
      this.dateRangeError.set(true);
      this.dateRangeErrorMessage.set('parking.request.validation.end_date_before_start');
    } else {
      this.dateRangeError.set(false);
      this.dateRangeErrorMessage.set('');
    }
  }
}
